import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority";
import { X, CheckCircle, AlertCircle } from "lucide-react"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

const toastStyles = `
  @keyframes slideInTopRight {
    from {
      opacity: 0;
      transform: translateX(400px) translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateX(0) translateY(0) scale(1);
    }
  }

  @keyframes slideOutTopRight {
    from {
      opacity: 1;
      transform: translateX(0) translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateX(400px) translateY(-20px) scale(0.95);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .toast-enter {
    animation: slideInTopRight 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .toast-exit {
    animation: slideOutTopRight 0.3s ease-in;
  }
`

const StyleSheet = () => (
  <style>{toastStyles}</style>
)

const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <>
    <StyleSheet />
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        "fixed right-0 top-0 z-[9999] flex max-h-screen w-full flex-col gap-3 p-6 sm:bottom-auto sm:right-0 sm:top-0 sm:flex-col md:max-w-[480px]",
        className
      )}
      {...props} />
  </>
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "toast-enter group pointer-events-auto relative w-full overflow-hidden rounded-xl border backdrop-blur-sm p-5 pr-8 shadow-2xl transition-all duration-300",
  {
    variants: {
      variant: {
        default: "border-teal-200/40 bg-gradient-to-br from-teal-50/95 to-cyan-50/95 text-slate-900 shadow-lg shadow-teal-500/10",
        destructive:
          "border-red-200/40 bg-gradient-to-br from-red-50/95 to-orange-50/95 text-red-900 shadow-lg shadow-red-500/10",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-teal-200/50 bg-teal-500/10 px-3 text-xs font-semibold text-teal-700 transition-all hover:bg-teal-500/20 hover:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500/30 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-red-200/50 group-[.destructive]:bg-red-500/10 group-[.destructive]:text-red-700 group-[.destructive]:hover:bg-red-500/20 group-[.destructive]:hover:border-red-300 group-[.destructive]:focus:ring-red-500/30",
      className
    )}
    {...props} />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-lg p-1.5 text-slate-400 transition-all duration-200 hover:bg-slate-200/40 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500/30 group-[.destructive]:text-red-400 group-[.destructive]:hover:bg-red-200/40 group-[.destructive]:hover:text-red-600 group-[.destructive]:focus:ring-red-500/30",
      className
    )}
    toast-close=""
    {...props}>
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("flex items-center gap-2 text-sm font-bold [&+div]:text-xs", className)}
    {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn("text-sm font-medium opacity-85 line-clamp-2", className)} {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export { ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction };
